import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { assistantAPI } from '../services/api_consolidated';


const routePrompts = {
  '/dashboard': [
    'Explique-moi le dashboard',
    "Que signifient les indicateurs d'activité ?",
  ],
  '/repairs': [
    'Comment créer un ticket de réparation ?',
    'Comment envoyer une réparation à la caisse ?',
  ],
  '/cashier': [
    'Comment encaisser une vente ?',
    'Comment retrouver un client ?',
  ],
  '/products': [
    'Comment gérer le stock faible ?',
    'Comment créer un produit ?',
  ],
  '/appointments': [
    'Comment synchroniser Wix ?',
    'Comment créer un ticket depuis un rendez-vous ?',
  ],
  '/purchases': [
    'Comment créer une commande fournisseur ?',
    'Quand le stock est-il mis à jour ?',
  ],
  '/finance': [
    'Comment saisir une charge ?',
    'Que montre le dashboard reporting ?',
  ],
};

export default function AppAssistant() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Je suis Izdiine brika. Je peux t'aider à utiliser Biciclo sur l'écran en cours.",
    },
  ]);

  const quickPrompts = useMemo(() => {
    const matchedEntry = Object.entries(routePrompts).find(([routePrefix]) =>
      location.pathname.startsWith(routePrefix)
    );
    return matchedEntry?.[1] || [
      "Comment fonctionne ce module ?",
      "Quelle est l'action la plus utile ici ?",
    ];
  }, [location.pathname]);

  const sendMessage = async (messageText) => {
    const trimmed = messageText.trim();
    if (!trimmed || loading) {
      return;
    }

    const nextMessages = [...messages, { role: 'user', content: trimmed }];
    setMessages(nextMessages);
    setInput('');
    setLoading(true);

    try {
      const response = await assistantAPI.chat({
        message: trimmed,
        route: location.pathname,
        history: messages.slice(-6),
      });

      setMessages([
        ...nextMessages,
        {
          role: 'assistant',
          content: response.data?.answer || "Je n'ai pas réussi à produire une réponse utile.",
        },
      ]);
    } catch (error) {
      setMessages([
        ...nextMessages,
        {
          role: 'assistant',
          content: error.response?.data?.error || "Je n'ai pas pu répondre pour l'instant.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-40">
      {open && (
        <div className="mb-4 w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-[28px] border border-slate-200 bg-white/95 shadow-[0_24px_80px_rgba(15,23,42,0.18)] backdrop-blur-xl">
          <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50/80 px-4 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white">
                <SparklesIcon className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">Izdiine brika</p>
                <p className="text-xs text-slate-500">Guide basé sur le code de l’application</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="rounded-full p-2 text-slate-400 hover:bg-white hover:text-slate-700"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="max-h-[440px] space-y-3 overflow-y-auto px-4 py-4">
            {messages.map((message, index) => (
              <div
                key={`${message.role}-${index}`}
                className={`rounded-2xl px-4 py-3 text-sm leading-6 ${
                  message.role === 'assistant'
                    ? 'bg-slate-100 text-slate-700'
                    : 'ml-8 bg-slate-900 text-white'
                }`}
              >
                {message.content}
              </div>
            ))}

            {loading && (
              <div className="rounded-2xl bg-slate-100 px-4 py-3 text-sm text-slate-500">
                Izdiine brika réfléchit…
              </div>
            )}
          </div>

          <div className="border-t border-slate-200 px-4 py-4">
            <div className="mb-3 flex flex-wrap gap-2">
              {quickPrompts.map((prompt) => (
                <button
                  key={prompt}
                  onClick={() => sendMessage(prompt)}
                  className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 hover:border-slate-300 hover:text-slate-900"
                >
                  {prompt}
                </button>
              ))}
            </div>

            <div className="flex items-end gap-2">
              <textarea
                rows={2}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(input);
                  }
                }}
                placeholder="Pose une question à Izdiine brika..."
                className="min-h-[56px] flex-1 resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-slate-400"
              />
              <button
                onClick={() => sendMessage(input)}
                disabled={loading || !input.trim()}
                className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-900 text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <PaperAirplaneIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((current) => !current)}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-900 text-white shadow-[0_18px_40px_rgba(15,23,42,0.28)] transition hover:scale-[1.03] hover:bg-slate-800"
      >
        <ChatBubbleLeftRightIcon className="h-6 w-6" />
      </button>
    </div>
  );
}
